import {
	LICENSED_PRODUCTS,
	getTenantContext_DEPRECATED_DO_NOT_USE,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { ActiveIncidentsServiceResponse } from '../../common/types.tsx';
import type { JiraSearchApiResponse } from './types.tsx';
import {
	mapIssuesToServicesWithActiveIncidents,
	emptyServicesWithActiveIncidentsObject,
	getFieldNameForJql,
	CF_SEPARATOR,
} from './utils.tsx';

const LOGGER_LOCATION = 'linked-object-incidents-store.fetchActiveIncidentsService';

const getQueryPayloadForIncidentsOnAffectedServices = (
	affectedServicesJQLFieldName: string,
	serviceIds: string[],
) => ({
	jql: `"Ticket category" = Incidents and resolution is EMPTY and ${affectedServicesJQLFieldName} in ("${serviceIds.join(
		'","',
	)}")`,
	...(fg('jql-search-api-migration-agni')
		? { fields: ['*navigable'], expand: 'renderedFields' }
		: { expand: ['renderedFields'] }),
});

const transformIssueSearchResponse = (
	affectedServicesFieldId: string,
	issueSearchResponse: JiraSearchApiResponse,
	serviceIds: string[],
): ActiveIncidentsServiceResponse => {
	if (issueSearchResponse.issues.length === 0) {
		return { data: emptyServicesWithActiveIncidentsObject(serviceIds) };
	}

	return {
		data: mapIssuesToServicesWithActiveIncidents(
			issueSearchResponse.issues,
			serviceIds,
			affectedServicesFieldId,
		),
	};
};

export const fetchActiveIncidentsService = async (
	affectedServicesFieldId: string,
	serviceIds: string[],
): Promise<ActiveIncidentsServiceResponse> => {
	try {
		if (serviceIds.length === 0) {
			return { data: {} };
		}

		const { licensedProducts } = getTenantContext_DEPRECATED_DO_NOT_USE();
		if (!licensedProducts[LICENSED_PRODUCTS.JIRA_SERVICEDESK]) {
			// 'Ticket category' is not available in JQL if JSM has been deleted from the site,
			// so there is no point making a request in this case.
			return { data: emptyServicesWithActiveIncidentsObject(serviceIds) };
		}

		if (!affectedServicesFieldId || !affectedServicesFieldId.startsWith(CF_SEPARATOR)) {
			logger.safeErrorWithoutCustomerData(
				LOGGER_LOCATION,
				`Format of affected services field ID was invalid - ${affectedServicesFieldId}`,
			);
			return { error: 'INVALID_RESPONSE' };
		}

		const response: JiraSearchApiResponse = await performPostRequest(
			fg('jql-search-api-migration-agni') ? '/rest/api/3/search/jql' : '/rest/api/3/search',
			{
				body: JSON.stringify(
					getQueryPayloadForIncidentsOnAffectedServices(
						getFieldNameForJql(affectedServicesFieldId),
						serviceIds,
					),
				),
			},
		);
		return transformIssueSearchResponse(affectedServicesFieldId, response, serviceIds);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.statusCode) {
			logger.safeErrorWithoutCustomerData(
				LOGGER_LOCATION,
				`Endpoint returned unexpected status code ${error.statusCode}`,
			);
			return { error: 'INTERNAL_ERROR' };
		}

		if (isClientFetchError(error)) {
			return { error: 'CLIENT_FETCH_ERROR' };
		}

		logger.unsafeErrorWithCustomerData(
			LOGGER_LOCATION,
			`Endpoint returned unexpected response: ${error.message}`,
		);
		return { error: 'VALIDATION_ERROR' };
	}
};
